import { useLazyQuery } from '@apollo/client';
import { List } from '@elipssolution/harfang';
import { ButtonBase, styled } from '@mui/material';
import { useCallback } from 'react';

import useCustomRouter from '../../../../src/hooks/useCustomRouter';
import { FETCH_DOCUMENT_UPLOADS, FetchDocumentUploadsType } from '../../api/documentUpload';
import MobileDocumentUploadListRow from '../../components/MobileDocumentUploadListRow';
import { DocumentUploadType } from '../../types/documentUpload';

const LIST_SIZE = 4;
const LIST_ITEM_HEIGHT = 60;
const SEE_MORE_ITEM_HEIGHT = 48;

const Container = styled('div')(({ theme: { palette, shadows, shape } }) => ({
	display: 'flex',
	flexDirection: 'column',
	height: `calc((${LIST_ITEM_HEIGHT}px * ${LIST_SIZE}) + ${SEE_MORE_ITEM_HEIGHT}px + 3px)`,
	borderRadius: shape.borderRadius * 2,
	border: `1px solid ${palette.divider}`,
	boxShadow: shadows[1],
	overflow: 'hidden',

	'& > div:first-of-type': {
		flex: 1,
		borderRadius: shape.borderRadius * 2,
		border: 'none',
		boxShadow: 'none',
	},
}));

const SeeMoreItem = styled(ButtonBase)(({ theme: { palette, typography } }) => ({
	borderTop: `1px solid ${palette.divider}`,
	height: `${SEE_MORE_ITEM_HEIGHT}px`,
	display: 'flex',
	alignItems: 'center',
	justifyContent: 'center',
	fontWeight: 600,
	fontSize: typography.body2.fontSize,
}));

const MobileDocumentUploadsHistory = () => {
	const { push } = useCustomRouter();

	const [fetchDocumentUploads] = useLazyQuery<FetchDocumentUploadsType>(FETCH_DOCUMENT_UPLOADS);

	const documentUploadsDataSource = useCallback(async () => {
		const { data, error } = await fetchDocumentUploads({
			variables: {
				page: {
					limit: LIST_SIZE,
					offset: 0,
				},
			},
		});

		if (error) {
			throw error;
		}

		const {
			document_documentUploads: { items = [], count = 0 },
		} = data ?? { document_documentUploads: {} };

		return {
			items: items.map(({ createdAt, ...rest }) => ({ createdAt: new Date(createdAt), ...rest })),
			count: Math.min(LIST_SIZE, count),
		};
	}, [fetchDocumentUploads]);

	const handleDocumentsNavigation = useCallback(() => {
		push('/document/storageUpload').catch((navigationError) => {
			throw navigationError;
		});
	}, [push]);

	return (
		<Container>
			<List<DocumentUploadType> dataSource={documentUploadsDataSource} rowRenderer={MobileDocumentUploadListRow} />

			<SeeMoreItem onClick={handleDocumentsNavigation}>Voir plus</SeeMoreItem>
		</Container>
	);
};

export default MobileDocumentUploadsHistory;
